import type { TaskLifecycleEventSummary } from "@/hooks/use-tasks";
import { formatMonthDay } from "@/lib/i18n";

interface CrmTaskHistoryPanelProps {
  history: TaskLifecycleEventSummary[];
  historyNextCursor: string | null;
  historyTotal: number | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  memberLabelById: Map<string, string>;
  onLoadMoreHistory: () => void;
}

const TASK_HISTORY_METADATA_EVIDENCE_FIELDS: Array<{ key: string; label: string }> = [
  { key: "resolutionEventId", label: "Resolution event" },
  { key: "sourceEventId", label: "Source event" },
  { key: "followUpTaskId", label: "Follow-up task" },
  { key: "createdFollowUpTaskId", label: "Follow-up task" },
  { key: "automationRuleId", label: "Automation rule" },
  { key: "sourceRuleId", label: "Source rule" },
  { key: "slaPolicyId", label: "SLA policy" },
  { key: "cancellationSource", label: "Cancellation source" },
];

export function CrmTaskHistoryPanel({
  history,
  historyNextCursor,
  historyTotal,
  isLoading,
  isLoadingMore,
  error,
  memberLabelById,
  onLoadMoreHistory,
}: CrmTaskHistoryPanelProps) {
  if (!isLoading && !error && history.length === 0) {
    return null;
  }

  return (
    <div className="mb-3 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-3">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
        Task history
      </p>
      {isLoading && (
        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">Loading task history...</p>
      )}
      {error && (
        <p className="mt-2 text-xs text-[var(--color-warning)]">Could not load task history.</p>
      )}
      {history.length > 0 && (
        <ol className="mt-3 space-y-2">
          {history.map((event) => {
            const detail = getCrmTaskHistoryDetail(event);
            const evidence = getCrmTaskHistoryEvidence(event, memberLabelById);
            return (
              <li key={event.id} className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-[var(--color-foreground)]">{getCrmTaskHistoryTitle(event)}</p>
                  <p className="text-[11px] text-[var(--color-muted-foreground)]">{getCrmTaskHistoryMeta(event, memberLabelById)}</p>
                </div>
                {detail && (
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{detail}</p>
                )}
                {evidence.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {evidence.map((entry) => (
                      <span
                        key={`${event.id}:${entry.label}:${entry.value}`}
                        className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-0.5 text-[11px] text-[var(--color-muted-foreground)]"
                      >
                        <span className="font-medium text-[var(--color-foreground)]">{entry.label}:</span> {entry.value}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {(history.length > 0 || historyNextCursor) && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] text-[var(--color-muted-foreground)]">
            Showing {history.length}{historyTotal !== null ? ` of ${historyTotal}` : ""} history events
          </p>
          {historyNextCursor && (
            <button
              type="button"
              onClick={onLoadMoreHistory}
              disabled={isLoadingMore}
              className="rounded-md border border-[var(--color-border)] px-2 py-1 text-xs font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-surface)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoadingMore ? "Loading history..." : "Load more history"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function getCrmTaskHistoryTitle(event: TaskLifecycleEventSummary): string {
  if (event.eventType === "auto_cancelled") return "Auto-cancelled";
  if (event.eventType === "auto_completed") return "Auto-completed";
  if (event.eventType === "followup_created") return "Follow-up created";
  return formatCrmTaskHistoryLabel(event.eventType) ?? event.eventType;
}

function getCrmTaskHistoryDetail(event: TaskLifecycleEventSummary): string | null {
  return event.reason?.trim()
    || event.notes?.trim()
    || formatCrmTaskHistoryMetadataValue(event.metadata.reason)
    || formatCrmTaskHistoryMetadataValue(event.metadata.summary)
    || null;
}

function getCrmTaskHistoryEvidence(
  event: TaskLifecycleEventSummary,
  memberLabelById: Map<string, string>,
): Array<{ label: string; value: string }> {
  const entries: Array<{ label: string; value: string }> = [];
  const addEntry = (label: string, value: string | null | undefined) => {
    const formatted = value?.trim();
    if (!formatted) return;
    if (entries.some((entry) => entry.label === label && entry.value === formatted)) return;
    entries.push({ label, value: formatted });
  };

  addEntry("Outcome", formatCrmTaskHistoryLabel(event.outcome) ?? event.outcome);
  if (event.previousAssignee !== event.nextAssignee) {
    const previous = formatCrmTaskHistoryAssignee(event.previousAssignee, memberLabelById) ?? "Unassigned";
    const next = formatCrmTaskHistoryAssignee(event.nextAssignee, memberLabelById) ?? "Unassigned";
    addEntry("Assignee", `${previous} -> ${next}`);
  }
  addEntry("Trigger ID", event.triggerEventId);

  for (const field of TASK_HISTORY_METADATA_EVIDENCE_FIELDS) {
    const formatted = formatCrmTaskHistoryMetadataValue(event.metadata[field.key]);
    addEntry(field.label, formatted ? (formatCrmTaskHistoryLabel(formatted) ?? formatted) : null);
  }

  return entries.slice(0, 6);
}

function getCrmTaskHistoryMeta(
  event: TaskLifecycleEventSummary,
  memberLabelById: Map<string, string>,
): string {
  const actorLabel = formatCrmTaskHistoryAssignee(event.actorId, memberLabelById);
  const parts = [
    event.previousStatus && event.nextStatus && event.previousStatus !== event.nextStatus
      ? `${formatCrmTaskHistoryLabel(event.previousStatus)} -> ${formatCrmTaskHistoryLabel(event.nextStatus)}`
      : formatCrmTaskHistoryLabel(event.nextStatus ?? event.previousStatus ?? ""),
    actorLabel ? `by ${actorLabel}` : null,
    event.triggerEventType ? `from ${formatCrmTaskHistoryLabel(event.triggerEventType) ?? event.triggerEventType}` : null,
    formatMonthDay(event.createdAt),
  ].filter((part): part is string => Boolean(part));

  return parts.join(" · ");
}

function formatCrmTaskHistoryAssignee(
  value: string | null | undefined,
  memberLabelById: Map<string, string>,
): string | null {
  if (!value) return null;
  return memberLabelById.get(value) ?? value;
}

function formatCrmTaskHistoryLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_.-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatCrmTaskHistoryMetadataValue(value: unknown): string | null {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const values = value.map(formatCrmTaskHistoryMetadataValue).filter(Boolean);
    return values.length > 0 ? values.join(", ") : null;
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const namedValue = formatCrmTaskHistoryMetadataValue(record.label)
      ?? formatCrmTaskHistoryMetadataValue(record.name)
      ?? formatCrmTaskHistoryMetadataValue(record.title);
    if (namedValue) return namedValue;

    const pairs = Object.entries(record)
      .map(([key, item]) => {
        const formatted = typeof item === "string"
          ? formatCrmTaskHistoryLabel(item)
          : formatCrmTaskHistoryMetadataValue(item);
        return formatted ? `${formatCrmTaskHistoryLabel(key) ?? key}: ${formatted}` : null;
      })
      .filter(Boolean);

    return pairs.length > 0 ? pairs.join("; ") : null;
  }
  return null;
}
